import { bffApi } from "../bff/bffApi";
import { UserDto } from "../../global/dataTransferObjects/user";

// Autenticação do portal pelo VAULT, sempre através do BFF (decisão D-B).
//
// O front nunca fala com o VAULT direto: o client_secret do app-client fica no
// BFF, como já acontece no fluxo do app. O systemCode ('portal') também é fixado
// lá — se viesse do browser, qualquer um pediria token para outro sistema.

/** Mesmo formato do TokenDto legado, para o provider de autenticação não mudar. */
export interface IPortalToken {
  token: string;
  createdAt: string;
  expirationDate: string;
}

export interface IPortalSession {
  token: IPortalToken;
  refreshToken: IPortalToken;
  /**
   * Token da API antiga, para as chamadas que o portal faz direto a ela — ela não
   * aceita o token do VAULT. Emitido pelo BFF no login e em cada renovação.
   */
  legacyToken?: IPortalToken;
  legacyRefreshToken?: IPortalToken;
  /** Só vem no login: a renovação não recarrega o perfil. */
  userInfo?: UserDto | null;
  businessList?: any;
  /** Teto absoluto da sessão (1 h). Atingido, o usuário refaz o login. */
  sessionExpiresAt: string;
}

/** Corpo das recusas do VAULT repassadas pelo BFF. */
export interface IPortalDenial {
  reason?: string;
  message?: string;
}

const routes = {
  login: "/auth/portal/login",
  impersonate: "/auth/portal/impersonate",
  refresh: "/auth/portal/refresh",
  logout: "/auth/portal/logout",
  resetRequest: "/auth/portal/reset-request",
  reset: "/auth/portal/reset",
};

export const login = async (email: string, password: string): Promise<IPortalSession> => {
  const { data } = await bffApi.post<IPortalSession>(routes.login, { email, password });
  return data;
};

/**
 * Acesso de suporte: abre a sessão da conta informada. A senha é a do próprio
 * operador, e o VAULT exige que ele tenha a permissão de acesso — a senha
 * compartilhada de suporte do fluxo antigo deixou de existir.
 */
export const impersonate = async (
  actorEmail: string,
  password: string,
  targetEmail: string
): Promise<IPortalSession> => {
  const { data } = await bffApi.post<IPortalSession>(routes.impersonate, {
    actorEmail,
    password,
    targetEmail,
  });

  return data;
};

export const refresh = async (refreshToken: string): Promise<IPortalSession> => {
  const { data } = await bffApi.post<IPortalSession>(routes.refresh, { refreshToken });
  return data;
};

export const logout = async (refreshToken: string): Promise<void> => {
  await bffApi.post(routes.logout, { refreshToken });
};

/**
 * Pede o e-mail de redefinição. Responde igual existindo ou não o login — não
 * revelar quais contas existem é requisito (RN60), então a tela dá sempre a
 * mesma mensagem.
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  await bffApi.post(routes.resetRequest, { email });
};

/** Consome o token do link do e-mail e grava a senha nova. */
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  await bffApi.post(routes.reset, { token, newPassword });
};

export default {
  login,
  impersonate,
  refresh,
  logout,
  requestPasswordReset,
  resetPassword,
};
